/**
 * LoginPage (`/login-pwd`) — password fallback for returning users.
 *
 * Design intent
 * - Centered single-column shell, mirroring `OnboardingLayout` so the entire
 *   auth flow feels like one continuous surface (favicon-lockup + soft top
 *   gradient + form card).
 * - No marketing panel: this URL is only hit by repeat users who already know
 *   the product. Marketing copy here is friction.
 * - Opinionated form: Email → Password. Email is now globally unique across
 *   tenants so the workspace selector that used to come first is gone.
 *   URL/localStorage prefill, auto-focus of the first empty field, password
 *   show/hide, and inline Caps-Lock hint are preserved.
 * - Pure design tokens — no `brandPalette` references, no raw hex.
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Stack,
  CircularProgress,
  Tooltip,
  Link,
  alpha,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import KeyboardCapslockRounded from "@mui/icons-material/KeyboardCapslockRounded";
import axios from "axios";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { buildApiUrl, config, tenantApiPrefix } from "../config";
import { apiCache } from "../utils/apiCache";
import { identifyUser, trackUserLoggedIn } from "../utils/analytics";

const API_URL = buildApiUrl("");
// localStorage key for "remember me" — separate from session keys so it
// survives logout. Bumped to v1 so we can change the shape later if needed.
const REMEMBER_KEY = "klaris_login_remember_v1";

type FormState = {
  tenant_name: string;
  email: string;
  password: string;
};

// We keep `tenant` in the remembered shape because long-time users
// expect to see their workspace name pre-filled even though it's now
// just a sanity check (email is globally unique).
type Remembered = { tenant?: string; email?: string };

function readRemembered(): Remembered | null {
  try {
    const raw = window.localStorage.getItem(REMEMBER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed as Remembered;
  } catch {
    return null;
  }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get("email");

  const [form, setForm] = useState<FormState>(() => {
    const remembered = readRemembered();
    return {
      tenant_name: remembered?.tenant || "",
      email: emailFromUrl || remembered?.email || "",
      password: "",
    };
  });
  const [remember, setRemember] = useState<boolean>(() => !!readRemembered());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Auto-focus the first *empty* field — skips fields we pre-filled so the
  // user can start typing immediately on the field that actually needs input.
  useEffect(() => {
    if (!form.email) emailRef.current?.focus();
    else passwordRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError(null);
  };

  const handlePasswordKey = (e: React.KeyboardEvent) => {
    // `getModifierState` is only available on KeyboardEvent — narrow safely.
    const native = e as unknown as { getModifierState?: (k: string) => boolean };
    if (typeof native.getModifierState === "function") {
      setCapsLockOn(native.getModifierState("CapsLock"));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.email || !form.password) {
      setError("Email and password are required.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/login`, form);
      apiCache.clear();
      window.localStorage.setItem("klaris_jwt", res.data.access_token);
      window.localStorage.setItem("klaris_user", JSON.stringify(res.data.user));
      window.localStorage.setItem("klaris_tenant", JSON.stringify(res.data.tenant));

      // Persist (or clear) remember-me snapshot for next visit.
      if (remember) {
        window.localStorage.setItem(
          REMEMBER_KEY,
          JSON.stringify({
            tenant: form.tenant_name.trim() || undefined,
            email: form.email,
          }),
        );
      } else {
        window.localStorage.removeItem(REMEMBER_KEY);
      }

      window.dispatchEvent(new Event("auth-change"));
      identifyUser(res.data.user, res.data.tenant);
      trackUserLoggedIn();

      // Resolve the configured home page so login lands users where their
      // tenant has chosen to start.
      let homePage = "copilot";
      try {
        const tid = res.data.tenant?.tenant_id;
        if (tid) {
          const brandingResp = await fetch(
            buildApiUrl(`${tenantApiPrefix(String(tid))}/branding`),
            {
              headers: { Authorization: `Bearer ${res.data.access_token}` },
              cache: "no-store",
            } as RequestInit,
          );
          if (brandingResp.ok) {
            const brandingData = await brandingResp.json();
            homePage = brandingData.defaultHomePage || "copilot";
            if (homePage === "chat" && !brandingData.showLegacyVisualizer) {
              homePage = "copilot";
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch branding for home page:", err);
      }

      const homeRoute =
        homePage === "dashboards"
          ? "/dashboard"
          : homePage === "spaces"
            ? "/spaces"
            : homePage === "copilot"
              ? "/copilot"
              : "/chat";
      navigate(homeRoute, { replace: true });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(
        e?.response?.data?.detail ||
          "Sign-in failed. Check your email and password.",
      );
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <Box
      sx={(t) => ({
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: t.tokens.surface.canvas,
        // Soft brand-tinted halo at the top so the page reads as part of the
        // auth surface, not a generic system background.
        backgroundImage: `radial-gradient(ellipse 100% 55% at 50% -15%, ${alpha(
          t.tokens.brand.primary,
          t.palette.mode === "light" ? 0.12 : 0.10,
        )}, transparent)`,
        px: { xs: 2, md: 4 },
        pt: { xs: 4, sm: 6, md: 8 },
        pb: { xs: 4, sm: 6, md: 8 },
        animation: "loginFadeIn 240ms cubic-bezier(0.16, 1, 0.3, 1)",
        "@keyframes loginFadeIn": {
          from: { opacity: 0, transform: "translateY(6px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      })}
    >
      {/* Brand lockup — same pattern as OnboardingLayout */}
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, mb: 3 }}>
        <Box
          component="img"
          src={config.faviconUrl}
          alt={config.brandName}
          sx={{ width: 32, height: 32, borderRadius: "7px" }}
        />
        <Typography
          variant="h5"
          sx={(t) => ({
            fontWeight: 700,
            letterSpacing: "-0.012em",
            color: t.tokens.text.primary,
          })}
        >
          {config.brandName}
        </Typography>
      </Box>

      <Box sx={{ width: "100%", maxWidth: 420 }}>
        {/* Heading */}
        <Stack spacing={0.75} sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            variant="h4"
            sx={(t) => ({
              fontWeight: 700,
              letterSpacing: "-0.018em",
              color: t.tokens.text.primary,
            })}
          >
            Welcome back
          </Typography>
          <Typography
            variant="body2"
            sx={(t) => ({ color: t.tokens.text.secondary })}
          >
            Sign in with your workspace, email, and password.
          </Typography>
        </Stack>

        {/* Form card */}
        <Box
          sx={(t) => ({
            bgcolor: t.tokens.surface.raised,
            borderRadius: `${t.ds.radius.lg}px`,
            border: `1px solid ${t.tokens.border.subtle}`,
            boxShadow: t.tokens.shadow.sm,
            p: { xs: 2.5, sm: 3.5 },
          })}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <form onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Workspace"
                name="tenant_name"
                value={form.tenant_name}
                onChange={handleChange}
                autoComplete="organization"
                placeholder="acme"
                size="medium"
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                inputRef={emailRef}
                autoComplete="email"
                size="medium"
              />
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={handleChange}
                onKeyDown={handlePasswordKey}
                onKeyUp={handlePasswordKey}
                required
                inputRef={passwordRef}
                autoComplete="current-password"
                size="medium"
                helperText={
                  capsLockOn ? (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <KeyboardCapslockRounded sx={{ fontSize: 14 }} />
                      Caps Lock is on
                    </Box>
                  ) : undefined
                }
                FormHelperTextProps={{
                  sx: (t) => ({
                    color: capsLockOn ? t.tokens.semantic.warning : undefined,
                    fontWeight: capsLockOn ? 500 : undefined,
                  }),
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={showPw ? "Hide password" : "Show password"}
                      >
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPw((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPw ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                sx={{ mt: -0.5, ml: -0.5 }}
                control={
                  <Checkbox
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={(t) => ({ color: t.tokens.text.secondary })}
                  >
                    Remember me on this device
                  </Typography>
                }
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress
                      size={16}
                      thickness={5}
                      sx={{ color: "inherit" }}
                    />
                  ) : undefined
                }
                sx={{ py: 1.25, fontWeight: 600, mt: 0.5 }}
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </Stack>
          </form>
        </Box>

        {/* Secondary path — passwordless via email code. Doubles as the
            recovery flow for forgotten passwords. */}
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <Box
              sx={(t) => ({
                flex: 1,
                height: "1px",
                bgcolor: t.tokens.border.subtle,
              })}
            />
            <Typography
              variant="caption"
              sx={(t) => ({
                color: t.tokens.text.tertiary,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 700,
              })}
            >
              or
            </Typography>
            <Box
              sx={(t) => ({
                flex: 1,
                height: "1px",
                bgcolor: t.tokens.border.subtle,
              })}
            />
          </Stack>
          <Button
            component={RouterLink}
            to="/login"
            variant="outlined"
            fullWidth
            size="large"
            endIcon={<ArrowForwardRounded sx={{ fontSize: 18 }} />}
            sx={{ py: 1.15, fontWeight: 600 }}
          >
            Sign in with email code
          </Button>
          <Typography
            variant="caption"
            sx={(t) => ({
              display: "block",
              mt: 1.5,
              color: t.tokens.text.tertiary,
            })}
          >
            Forgot your password? Use an email code instead.
          </Typography>
        </Box>

        {/* Inline legal microcopy — replaces the global fixed Footer so this
            page can stand alone without surface conflicts. */}
        <Stack
          direction="row"
          spacing={1.25}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 4, flexWrap: "wrap" }}
        >
          <Typography
            variant="caption"
            sx={(t) => ({ color: t.tokens.text.tertiary })}
          >
            © {year} {config.companyName}
          </Typography>
          {config.privacyUrl && (
            <>
              <Box
                sx={(t) => ({
                  width: 3,
                  height: 3,
                  borderRadius: "50%",
                  bgcolor: t.tokens.text.tertiary,
                  opacity: 0.5,
                })}
              />
              <Link
                href={config.privacyUrl}
                target="_blank"
                rel="noopener"
                underline="hover"
                variant="caption"
                sx={(t) => ({ color: t.tokens.text.tertiary })}
              >
                Privacy
              </Link>
            </>
          )}
          {config.termsUrl && (
            <>
              <Box
                sx={(t) => ({
                  width: 3,
                  height: 3,
                  borderRadius: "50%",
                  bgcolor: t.tokens.text.tertiary,
                  opacity: 0.5,
                })}
              />
              <Link
                href={config.termsUrl}
                target="_blank"
                rel="noopener"
                underline="hover"
                variant="caption"
                sx={(t) => ({ color: t.tokens.text.tertiary })}
              >
                Terms
              </Link>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
